import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BACKEND_URL } from '../lib/backendUrl';
import { useAuth } from '../contexts/AuthContext';
import { isClubAdmin, isSuperAdmin } from '../utils/roleUtils';
import { ROLE_DASHBOARD_MAP } from '../constants/roles';
import { CalendarIcon, MoneyIcon, ShareIcon, EditIcon, ClipboardIcon, BuildingIcon, LinkIcon, CloseIcon, TwitterIcon, FacebookIcon, LinkedInIcon, WhatsAppIcon, ArrowLeftIcon } from './Icons';
import '../styles/EventTemplate.css';

interface Event {
  id: string;
  name: string;
  description: string;
  type?: 'event' | 'workshop' | 'exhibition' | 'seminar' | 'competition' | 'hackathon' | 'conference' | 'other';
  image?: string;
  date?: string;
  created_by?: string;
  club_id?: string;
  club_name?: string;
  fee?: number;
  created_at?: string;
  createdBy?: string;
  clubId?: string;
  clubName?: string;
  createdAt?: string;
  details?: string;
}

interface CustomField {
  headline: string;
  value: string;
}

interface TimelinePhase {
  phase: string;
  date: string;
  description: string;
}

interface EventDetails {
  location?: string;
  registrationDeadline?: string;
  eligibility?: string;
  teamSize?: number;
  customFields?: CustomField[];
  timeline?: TimelinePhase[];
}

const EventTemplate: React.FC = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [checkingEnrollment, setCheckingEnrollment] = useState(false);
  const [eventDetails, setEventDetails] = useState<EventDetails>({});

  const API_URL = BACKEND_URL;

  useEffect(() => {
    fetchEvent();
    if (token) {
      checkUserEnrollment();
    }
  }, [eventId, token]);

  const parseDetails = (raw?: string): EventDetails => {
    if (!raw) return {};
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  };

  const fetchEvent = async () => {
    try {
      const endpoint = token
        ? `${API_URL}/api/events/${eventId}`
        : `${API_URL}/public/events/${eventId}`;
      const config = token
        ? { headers: { Authorization: `Bearer ${token}` } }
        : {};
      const response = await axios.get(endpoint, config);
      if (response.data) {
        const d = response.data as Event;
        d.type = d.type || 'event';
        d.fee = d.fee ?? 0;
        d.date = d.date || new Date().toISOString();
        d.created_at = d.created_at || d.createdAt || new Date().toISOString();
        d.created_by = d.created_by || d.createdBy || 'Unknown';
        d.club_name = d.club_name || d.clubName || 'College';
        setEventDetails(parseDetails(d.details));
        setEvent(d);
        setError(null);
      } else {
        setError('No event data received');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load event');
    } finally {
      setLoading(false);
    }
  };

  const checkUserEnrollment = async () => {
    try {
      setCheckingEnrollment(true);
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API_URL}/api/registrations/me`, config);
      const userRegistrations = response.data || [];
      const isUserEnrolled = userRegistrations.some((reg: any) => reg.event_id === eventId);
      setIsEnrolled(isUserEnrolled);
    } catch (err) {
      console.error('Error checking enrollment:', err);
      setIsEnrolled(false);
    } finally {
      setCheckingEnrollment(false);
    }
  };

  const handleShare = (platform: string) => {
    if (!event) return;
    const url = `${window.location.origin}/public/events/${eventId}`;
    const text = `Check out this event: ${event.name}`;
    const actions: Record<string, () => void> = {
      twitter: () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank'),
      facebook: () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank'),
      linkedin: () => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank'),
      whatsapp: () => window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`, '_blank'),
      copy: () => { navigator.clipboard.writeText(url); alert('Link copied!'); },
    };
    actions[platform]?.();
    setShareMenuOpen(false);
  };

  const handleEnrollSubmit = async (data: any) => {
    try {
      // Validate token exists
      if (!token) {
        alert('Session expired. Please log in again to register for this event.');
        navigate('/auth');
        return;
      }

      const payload = {
        event_id: eventId,
        ...data
      };

      const response = await axios.post(`${API_URL}/api/registrations`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert('Registration confirmed! Please check your email and dashboard wallet.');
      setShowEnrollModal(false);
      fetchEvent();
      setIsEnrolled(true); // Update enrollment status immediately
    } catch (err: any) {
      console.error('Error enrolling:', err);
      
      // Handle specific error cases
      if (err.response?.status === 401) {
        alert('Your session has expired. Please log in again to register.');
        navigate('/auth');
      } else if (err.response?.status === 403) {
        alert('You do not have permission to enroll in this event.');
      } else if (err.response?.status === 400) {
        alert(err.response?.data?.message || 'Invalid enrollment data. Please check your information.');
      } else {
        alert(err.response?.data?.message || 'Failed to enroll in the event. Please try again.');
      }
    }
  };

  if (loading) {
    return (
      <div className="event-template-container">
        <div className="et-loading">Loading event details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="event-template-container">
        <div className="et-error">
          <h2>Error Loading Event</h2>
          <p>{error}</p>
          <button className="et-back-btn" onClick={() => navigate(-1)}><ArrowLeftIcon size={14} /> Go Back</button>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="event-template-container">
        <div className="et-error">
          <h2>Event Not Found</h2>
          <p>The event you're looking for doesn't exist.</p>
          <button className="et-back-btn" onClick={() => navigate(-1)}><ArrowLeftIcon size={14} /> Go Back</button>
        </div>
      </div>
    );
  }

  const canEdit = user && (
    user.id === (event.created_by || event.createdBy) ||
    isClubAdmin(user.role) ||
    isSuperAdmin(user.role)
  );

  const getEditPath = () => {
    if (!eventId) return '/';
    if (!user?.role) return `/events/${eventId}`;
    const normalizedRole = user.role.toUpperCase().replace('-', '_') as keyof typeof ROLE_DASHBOARD_MAP;
    const dashboardPath = ROLE_DASHBOARD_MAP[normalizedRole];
    return `${dashboardPath || '/dashboard/club'}/events/${eventId}/edit`;
  };

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  const formatTimelineDate = (d: string) => {
    if (!d) return 'TBA';
    const parsed = new Date(d.replace(' ', 'T'));
    if (Number.isNaN(parsed.getTime())) return d;
    return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <motion.div
      className="event-template-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Back */}
      <button className="et-back-btn" onClick={() => navigate(-1)}>
        <ArrowLeftIcon size={14} /> Back to Events
      </button>

      {/* Header */}
      <div className="et-header">
        <div className="et-image-section">
          {event.image ? (
            <img src={event.image} alt={event.name} className="et-image" />
          ) : (
            <div className="et-placeholder">
              <span className="et-placeholder-text">{event.name}</span>
            </div>
          )}
        </div>

        <div className="et-info">
          <div className="et-meta-top">
            <span className="et-type-badge">{(event.type || 'event').toUpperCase()}</span>
            <span className="et-club-name">{event.club_name || 'Event'}</span>
          </div>

          <div className="et-title-section">
            <h1 className="et-title">{event.name}</h1>
            <div className="et-actions">
              {canEdit && (
                <motion.button
                  className="et-edit-btn"
                  onClick={() => navigate(getEditPath())}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <EditIcon size={14} /> Edit
                </motion.button>
              )}
              <div className="et-share-container">
                <motion.button
                  className="et-share-btn"
                  onClick={() => setShareMenuOpen(!shareMenuOpen)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <ShareIcon size={14} /> Share
                </motion.button>
                <AnimatePresence>
                  {shareMenuOpen && (
                    <motion.div
                      className="et-share-menu"
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                    >
                      <button onClick={() => handleShare('twitter')}><TwitterIcon size={14} /> Twitter</button>
                      <button onClick={() => handleShare('facebook')}><FacebookIcon size={14} /> Facebook</button>
                      <button onClick={() => handleShare('linkedin')}><LinkedInIcon size={14} /> LinkedIn</button>
                      <button onClick={() => handleShare('whatsapp')}><WhatsAppIcon size={14} /> WhatsApp</button>
                      <button onClick={() => handleShare('copy')}><LinkIcon size={14} /> Copy Link</button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>

          <div className="et-quick-info">
            <div className="et-info-item">
              <span className="et-info-icon"><CalendarIcon size={16} color="#00ffff" /></span>
              <span>{event.date ? formatDate(event.date) : 'Date TBA'}</span>
            </div>
            <div className="et-info-item">
              <span className="et-info-icon"><MoneyIcon size={16} color="#00ffff" /></span>
              <span>Fee: {event.fee ? `₹${event.fee}` : 'Free'}</span>
            </div>
          </div>

          {token ? (
            isEnrolled ? (
              <motion.button
                className="et-cta enrolled"
                disabled
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                ✓ Already Enrolled
              </motion.button>
            ) : (
              <motion.button
                className="et-cta"
                onClick={() => setShowEnrollModal(true)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Register Now
              </motion.button>
            )
          ) : (
            <motion.button
              className="et-cta"
              onClick={() => navigate('/auth')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Sign In to Register
            </motion.button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="et-tabs">
        <button
          className={`et-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`et-tab ${activeTab === 'details' ? 'active' : ''}`}
          onClick={() => setActiveTab('details')}
        >
          Details
        </button>
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'overview' && (
          <motion.div
            key="overview"
            className="et-tab-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <h2>About this Event</h2>
            <p>{event.description}</p>

            <div className="et-details-grid" style={{ marginTop: '1rem' }}>
              {eventDetails.location && (
                <div className="et-detail-card">
                  <span className="et-detail-icon"><BuildingIcon size={20} color="#00ffff" /></span>
                  <div>
                    <p className="et-detail-label">Location</p>
                    <p className="et-detail-value">{eventDetails.location}</p>
                  </div>
                </div>
              )}

              {eventDetails.teamSize && (
                <div className="et-detail-card">
                  <span className="et-detail-icon"><ClipboardIcon size={20} color="#00ffff" /></span>
                  <div>
                    <p className="et-detail-label">Team Size</p>
                    <p className="et-detail-value">{eventDetails.teamSize}</p>
                  </div>
                </div>
              )}

              {eventDetails.registrationDeadline && (
                <div className="et-detail-card">
                  <span className="et-detail-icon"><CalendarIcon size={20} color="#00ffff" /></span>
                  <div>
                    <p className="et-detail-label">Registration Deadline</p>
                    <p className="et-detail-value">{formatDate(eventDetails.registrationDeadline)}</p>
                  </div>
                </div>
              )}
            </div>

            {eventDetails.eligibility && (
              <div style={{ marginTop: '1rem' }}>
                <h3>Eligibility</h3>
                <p className="et-prewrap">{eventDetails.eligibility}</p>
              </div>
            )}

            {eventDetails.timeline && eventDetails.timeline.length > 0 && (
              <div className="et-timeline-wrap">
                <h3 className="et-timeline-heading">Timeline</h3>
                <p className="et-timeline-subtitle">Every great mission has a sequence. Here&apos;s yours.</p>
                <div className="et-timeline-grid">
                  {eventDetails.timeline.map((item, index) => (
                    <div
                      className={`et-timeline-row ${index % 2 === 0 ? 'right' : 'left'}`}
                      key={`${item.phase}-${item.date}-${index}`}
                    >
                      <div className="et-timeline-node">
                        <span>{String(index + 1).padStart(2, '0')}</span>
                      </div>
                      <div className="et-timeline-card">
                        <p className="et-timeline-badge">{item.phase || 'Phase'}</p>
                        <h4>{formatTimelineDate(item.date)}</h4>
                        <p className="et-prewrap">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {eventDetails.customFields && eventDetails.customFields.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <h3>Additional Information</h3>
                <div className="et-details-grid">
                  {eventDetails.customFields.map((field, index) => (
                    <div className="et-detail-card" key={`${field.headline}-${index}`}>
                      <span className="et-detail-icon"><LinkIcon size={20} color="#00ffff" /></span>
                      <div>
                        <p className="et-detail-label">{field.headline}</p>
                        <p className="et-detail-value et-prewrap">{field.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
        {activeTab === 'details' && (
          <motion.div
            key="details"
            className="et-tab-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <h2>Event Information</h2>
            <div className="et-details-grid">
              <div className="et-detail-card">
                <span className="et-detail-icon"><CalendarIcon size={20} color="#00ffff" /></span>
                <div>
                  <p className="et-detail-label">Date</p>
                  <p className="et-detail-value">{event.date ? formatDate(event.date) : 'TBA'}</p>
                </div>
              </div>
              <div className="et-detail-card">
                <span className="et-detail-icon"><MoneyIcon size={20} color="#00ffff" /></span>
                <div>
                  <p className="et-detail-label">Registration Fee</p>
                  <p className="et-detail-value">{event.fee ? `₹${event.fee}` : 'Free'}</p>
                </div>
              </div>
              <div className="et-detail-card">
                <span className="et-detail-icon"><ClipboardIcon size={20} color="#00ffff" /></span>
                <div>
                  <p className="et-detail-label">Event Type</p>
                  <p className="et-detail-value">{(event.type || 'Event').charAt(0).toUpperCase() + (event.type || 'Event').slice(1)}</p>
                </div>
              </div>
              <div className="et-detail-card">
                <span className="et-detail-icon"><BuildingIcon size={20} color="#00ffff" /></span>
                <div>
                  <p className="et-detail-label">Organized By</p>
                  <p className="et-detail-value">{event.club_name || 'College'}</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Enrollment Modal */}
      <AnimatePresence>
        {showEnrollModal && (
          <EnrollmentModal
            event={event}
            onClose={() => setShowEnrollModal(false)}
            onSubmit={handleEnrollSubmit}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/* ============================================
   Enrollment Modal Sub-component
   ============================================ */
interface EnrollmentModalProps {
  event: Event;
  onClose: () => void;
  onSubmit: (data: any) => void;
}

const EnrollmentModal: React.FC<EnrollmentModalProps> = ({ event, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '', email: '', rollNumber: '', year: '', branch: '',
  });

  return (
    <motion.div
      className="et-modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="et-modal"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="et-modal-close" onClick={onClose}><CloseIcon size={16} /></button>

        <h2>Register for {event.name}</h2>
        <p className="et-modal-subtitle">
          {event.type === 'hackathon'
            ? 'Team registration — add members after sign-up'
            : 'Individual registration for this event'}
        </p>

        <form
          className="et-form"
          onSubmit={(e) => { e.preventDefault(); onSubmit(formData); }}
        >
          <div className="et-form-group">
            <label>Full Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Your full name"
              required
            />
          </div>
          <div className="et-form-group">
            <label>Email *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="your.email@example.com"
              required
            />
          </div>
          <div className="et-form-group">
            <label>Roll Number</label>
            <input
              type="text"
              value={formData.rollNumber}
              onChange={(e) => setFormData({ ...formData, rollNumber: e.target.value })}
              placeholder="e.g., 2024001"
            />
          </div>
          <div className="et-form-row">
            <div className="et-form-group">
              <label>Year</label>
              <select value={formData.year} onChange={(e) => setFormData({ ...formData, year: e.target.value })}>
                <option value="">Select Year</option>
                <option>1st Year</option>
                <option>2nd Year</option>
                <option>3rd Year</option>
                <option>4th Year</option>
              </select>
            </div>
            <div className="et-form-group">
              <label>Branch</label>
              <input
                type="text"
                value={formData.branch}
                onChange={(e) => setFormData({ ...formData, branch: e.target.value })}
                placeholder="e.g., CSE, ECE"
              />
            </div>
          </div>
          <div className="et-form-actions">
            <button type="button" className="et-cancel-btn" onClick={onClose}>Cancel</button>
            <motion.button
              type="submit"
              className="et-submit-btn"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Register
            </motion.button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default EventTemplate;
